/**
 * All protocol and replay types — inlined for pure-static deployment.
 * No external imports from benchmark root.
 */

// ─── Benchmark Mode ─────────────────────────────────────────

export type BenchmarkMode = "baseline" | "treatment" | "autonomous";
export type PaneMode = "baseline" | "treatment";

export function toPaneMode(mode: BenchmarkMode): PaneMode {
  return mode === "baseline" ? "baseline" : "treatment";
}

// ─── Scenario & Run Info ─────────────────────────────────────

export type ScenarioInfo = {
  id: string;
  name: string;
  inputFileCount: number;
};

export type RunInfo = {
  id: string;
  scenario: string;
  timestamp: string;
  commitHash: string;
  totalDurationMs: number;
};

// ─── Timeline Events ────────────────────────────────────────

export type TimelineEvent = {
  id: string;
  elapsed: number;
  kind:
    | "lifecycle-start"
    | "lifecycle-end"
    | "lifecycle-error"
    | "tool-start"
    | "tool-done"
    | "tool-error"
    | "prompt"
    | "assistant"
    | "subagent-spawn"
    | "subagent-end"
    | "autonomous-plan"
    | "validation"
    | "info";
  actor: string;
  isSubagent: boolean;
  parentSpawnId?: string;
  toolName?: string;
  toolDetail?: string;
  toolArgs?: unknown;
  toolResult?: unknown;
  toolDurationMs?: number;
  text?: string;
  validationOk?: boolean;
  validationCommand?: string;
  raw?: unknown;
};

// ─── Usage & Results ─────────────────────────────────────────

export type UsageTotals = {
  input: number;
  output: number;
  cacheRead: number;
  cacheWrite: number;
  totalTokens: number;
  totalCost: number;
};

export type ModelUsageEntry = {
  provider: string;
  model: string;
  count: number;
  totals: UsageTotals;
};

export type ModeResult = {
  mode: BenchmarkMode;
  durationMs: number;
  totals: UsageTotals | null;
  byModel: ModelUsageEntry[];
  validation: Array<{ command: string; ok: boolean; durationMs: number }>;
};

export type ComparisonResult = {
  tokenSaving: number;
  costSaving: number;
  timeSaving: number;
  baseline: ModeResult;
  treatment: ModeResult;
};

// ─── Replay Types ────────────────────────────────────────────

export type ReplayMeta = {
  schemaVersion: number;
  scenario: string;
  timestamp: string;
  commitHash: string;
  totalDurationMs: number;
};

export type ReplayEntry = {
  replayTs: number;
  msg: ServerMessage;
};

export type ReplayFile = {
  meta: ReplayMeta;
  entries: ReplayEntry[];
};

// ─── Server Messages ─────────────────────────────────────────

export type ServerMessage =
  | { type: "scenarios"; data: ScenarioInfo[] }
  | { type: "gateway-status"; online: boolean }
  | { type: "event"; mode: BenchmarkMode; data: TimelineEvent }
  | { type: "progress"; mode: BenchmarkMode; phase: string; elapsed: number }
  | { type: "result"; mode: BenchmarkMode; data: ModeResult }
  | { type: "comparison"; data: ComparisonResult }
  | { type: "error"; message: string }
  | { type: "runs"; data: RunInfo[] }
  | { type: "replay-start"; meta: ReplayMeta; demoMode: boolean }
  | {
      type: "replay-progress";
      position: number;
      elapsed: number;
      total: number;
      speed: number;
      paused: boolean;
      demoMode: boolean;
    }
  | { type: "replay-end" }
  | { type: "replay-seek" };

// ─── Helpers ─────────────────────────────────────────────────

export function normalizeReplayFile(file: ReplayFile): ReplayFile {
  return {
    meta: {
      ...file.meta,
      schemaVersion:
        typeof file.meta.schemaVersion === "number"
          ? file.meta.schemaVersion
          : 1,
    },
    entries: Array.isArray(file.entries) ? file.entries : [],
  };
}
